import React, { useState } from "react";
import Versus from "./Cards/versus.js";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import Comments from "./Cards/comments.js";
import { convertUTCDateToLocalDateTime } from "../utils/localtime.js";
import { styles } from "../styles/styles.js";
import { isNum, EM_DASH } from "../utils/format.js";
import { useAppState } from "../AppStateContext.js";
import { useHapticNavigate as useNavigate } from "../utils/haptics";
import * as motion from "motion/react-client";
import Q2Badge from "./Q2Badge.js";
import { picksOf, formatPick, resultColor, resultTitle } from "../utils/picks.js";

// An unpriced market rendered as a blank cell, or as a bare "(  )", reads as a
// formatting glitch rather than as missing data. It is missing data, and it is
// the normal condition for leagues no configured book prices.
const price = (value) => (isNum(value) ? value : EM_DASH);
const line = (value, priceValue) =>
  isNum(value) ? `${value} (${price(priceValue)})` : EM_DASH;

const Game = ({
  fixture,
  isAuthenticated,
  // Operator affordances. All default to off, so a signed-out visitor's slate
  // renders byte-for-byte as it did before any of this existed.
  showQ2 = false,
  q2State = null,
  q2Requested = false,
  selectable = false,
  selected = false,
  onToggleSelect,
  q2Policy = null,
  // The slate day this card was rendered on, carried to the fixture page so its
  // back-link returns to the same day instead of guessing one.
  slateDate = null,
  // This game's picks, for any signed-in visitor -- the gate SuggestionsBox
  // uses. Off by default for the same reason as the operator props above.
  showPicks = false,
}) => {
  const { isTutorialMode } = useAppState();
  const navigate = useNavigate();
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const picks = showPicks ? picksOf(fixture) : [];

  const handleClick = () => {
    navigate(`/fixture/${fixture.id}`, { state: { fixture, isTutorialMode, slateDate } });
  };

  const handleCommentClick = (e) => {
    e.stopPropagation();
    if (isAuthenticated) {
      setIsCommentsOpen(true);
    }
  };

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0}}
        transition={{
          duration: 0.5,
          scale: { type: "spring", damping: 10, stiffness: 100, bounce: 0.5 }
        }}
      >
        <Card className="main" style={styles.main}>
          <Card.Body>
            <Card.Title>
              <b>
                {" "}
                <Versus fixture={fixture} /> at{" "}
                {convertUTCDateToLocalDateTime(fixture.game_time)}
              </b>
            </Card.Title>
            <Table className="main" bordered onClick={handleClick}>
              <tbody>
                <tr>
                  <td className="Odds" style={styles.Odds}> Home </td>
                  <td className="Odds" style={styles.Odds}> {fixture.home_team} </td>
                  <td className="Odds" style={fixture.recent && fixture.recent.home_spread === "True" ? styles.Suggested : styles.Odds}>
                    {line(fixture.home_spread, fixture.home_spread_price)}
                  </td>
                  <td className="Odds" style={fixture.recent && fixture.recent.home_ml === "True" ? styles.Suggested : styles.Odds}>
                    {price(fixture.home_moneyline)}
                  </td>
                  <td className="Odds" style={fixture.recent && fixture.recent.over === "True" ? styles.Suggested : styles.Odds}>
                    {line(fixture.over, fixture.over_price)}
                  </td>
                </tr>
                <tr>
                  <td className="Odds" style={styles.Odds}> Away </td>
                  <td className="Odds" style={styles.Odds}> {fixture.away_team} </td>
                  <td className="Odds" style={fixture.recent && fixture.recent.away_spread === "True" ? styles.Suggested : styles.Odds}>
                    {line(fixture.away_spread, fixture.away_spread_price)}
                  </td>
                  <td className="Odds" style={fixture.recent && fixture.recent.away_ml === "True" ? styles.Suggested : styles.Odds}>
                    {price(fixture.away_moneyline)}
                  </td>
                  <td className="Odds" style={fixture.recent && fixture.recent.under === "True" ? styles.Suggested : styles.Odds}>
                    {line(fixture.under, fixture.under_price)}
                  </td>
                </tr>
                {/* A draw has a moneyline and nothing else. The team, spread and
                    total cells are blanked and unboxed rather than em-dashed:
                    an em-dash is this app's word for "unknown", and these are
                    not unknown, they are inapplicable. See styles.drawBlank. */}
                {isNum(fixture.draw_moneyline) && (
                  <tr style={styles.drawRow}>
                    <td className="Odds" style={{ ...styles.Odds, ...styles.drawCell }}> Draw </td>
                    <td style={styles.drawBlank} />
                    <td style={styles.drawBlank} />
                    <td className="Odds" style={{ ...styles.Odds, ...styles.drawCell }}>
                      {price(fixture.draw_moneyline)}
                    </td>
                    <td style={styles.drawBlank} />
                  </tr>
                )}
              </tbody>
            </Table>
            
            {/* One row for the operator's badge and everyone's picks. A signed-in
                visitor who is not staff gets the picks alone; a game with no
                picks and no badge gets no row at all. */}
            {(showQ2 || picks.length > 0) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {showQ2 && selectable && (
                  <label
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px',
                             color: 'white', fontSize: '0.75rem', cursor: 'pointer' }}
                    // The card's own onClick navigates to the fixture. Without
                    // this the first click on the checkbox would tick the box
                    // AND leave the page.
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleSelect && onToggleSelect(fixture.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    Select
                  </label>
                )}
                {showQ2 && (
                  <Q2Badge state={q2State} requested={q2Requested} policy={q2Policy} />
                )}
                {/* The odds table's own px clamp, not rem: the root font drops to
                    7px at 390px, which put a rem-sized pick at ~5.6px. A rule
                    between picks, because the flex gap alone read as one
                    run-on line. No nowrap: an upset pick runs ~55 characters
                    and has to wrap at 390px rather than push the page
                    sideways. The title carries the result for anyone who
                    cannot rely on the colour. */}
                {picks.map((p, i) => (
                  <span
                    key={i}
                    className="slate-pick"
                    title={resultTitle(p.result)}
                    style={{
                      color: resultColor(p.result),
                      fontSize: styles.Odds.fontSize,
                      ...(i > 0 && {
                        borderLeft: '1px solid rgba(255,255,255,0.35)',
                        paddingLeft: '10px',
                      }),
                    }}
                  >
                    {formatPick(p)}
                  </span>
                ))}
              </div>
            )}

            {fixture.comment_count > 0 && (
              <>
                <span 
                  style={{ 
                    marginLeft: '10px', 
                    fontSize: 'clamp(15px, calc(8px + (4) * ((100vw - 300px) / (1200 - 320))), 40px)',
                    cursor: 'pointer' 
                  }}
                  onClick={handleCommentClick}
                >
                  💬 {fixture.comment_count}
                </span>
                {isCommentsOpen && isAuthenticated && (
                    <Comments
                      fixture={fixture}
                      isAuthenticated={isAuthenticated}
                      isOpen={isCommentsOpen}
                      toggle={() => setIsCommentsOpen(!isCommentsOpen)}
                    />
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </motion.div>
    </div>
  );
};

export default Game;